import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SectionHeader, LoadError } from '@/components/dashboard/MetricCard';
import { ReferralLinksCard } from '@/components/dashboard/ReferralLinksCard';
import { academyApi } from '@/services/academyApi';
import { useDashboard } from '@/context/DashboardContext';
import { Search, Download, ExternalLink, FileText, FileVideo, FileImage, File, LayoutTemplate, Link2, Lock, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSiteLang } from '@/components/site/SiteLang';

// Tipos actuales (PDF, Imagen, Vídeo, Plantilla, Enlace) y los antiguos del seed (MP4, PNG, ZIP, HTML, DOCX)
const typeIcons = {
  PDF: FileText, Imagen: FileImage, 'Vídeo': FileVideo, Plantilla: LayoutTemplate, Enlace: Link2,
  MP4: FileVideo, PNG: FileImage, ZIP: File, HTML: FileText, DOCX: FileText,
};
const typeColors = {
  PDF: '#ef4444', Imagen: '#3b82f6', 'Vídeo': '#8b5cf6', Plantilla: '#10b981', Enlace: '#c9a84c',
  MP4: '#8b5cf6', PNG: '#3b82f6', ZIP: '#f59e0b', HTML: '#10b981', DOCX: '#3b82f6',
};

/*
  Inglés británico (05/11/2026). Título, descripción y categoría de cada recurso vienen de la BD y se muestran
  tal cual (en inglés, con el aviso «Content delivered in Spanish»); el tipo se traduce porque es un valor fijo.
*/
const T = {
  es: {
    loading: 'Cargando recursos...',
    title: 'Recursos de marketing',
    subtitle: 'Material profesional para tu actividad comercial',
    links: 'Tus enlaces personales',
    downloads: 'Material descargable',
    search: 'Buscar recurso…',
    searchLabel: 'Buscar recurso',
    all: 'Todos',
    loadError: 'No hemos podido cargar el material.',
    empty: 'Aún no hay material disponible. Lo publicaremos aquí en cuanto esté listo.',
    noMatch: 'No hay recursos que coincidan con tu búsqueda.',
    clear: 'Quitar filtros',
    availableWith: (plan) => `Disponible con la membresía ${plan}`,
    availableMembers: 'Disponible para miembros',
    members: 'Miembros',
    open: 'Abrir', download: 'Descargar', soon: 'Próximamente',
    type: (v) => v,
    contentNote: null,
  },
  en: {
    loading: 'Loading resources…',
    title: 'Marketing resources',
    subtitle: 'Professional materials for your sales activity',
    links: 'Your personal links',
    downloads: 'Downloadable materials',
    search: 'Search resources…',
    searchLabel: 'Search resources',
    all: 'All',
    loadError: 'We couldn’t load the materials.',
    empty: 'No materials available yet. We’ll publish them here as soon as they’re ready.',
    noMatch: 'No resources match your search.',
    clear: 'Clear filters',
    availableWith: (plan) => `Available with the ${plan} membership`,
    availableMembers: 'Available to members',
    members: 'Members',
    open: 'Open', download: 'Download', soon: 'Coming soon',
    type: (v) => ({ Imagen: 'Image', 'Vídeo': 'Video', Plantilla: 'Template', Enlace: 'Link' }[v] || v),
    contentNote: 'Content delivered in Spanish',
  },
};

export default function DashboardMarketing() {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const { user } = useDashboard();
  const refCode = user?.referral_code || user?.referralCode;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Todos');
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    academyApi.resources()
      .then((r) => setItems(Array.isArray(r) ? r : []))
      .catch((e) => { console.error(e); setError(true); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const categories = ['Todos', ...new Set(items.map(r => r.category).filter(Boolean))];
  const q = search.toLowerCase();
  const filtered = items
    .filter(r => category === 'Todos' || r.category === category)
    .filter(r => (r.title || '').toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="animate-spin text-[#c9a84c]" size={28} />
        <span className="text-xs text-gray-400">{t.loading}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dashboard-marketing">
      <SectionHeader title={t.title} subtitle={t.subtitle} />

      {refCode && (
        <section>
          <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-3">
            {t.links}
          </h3>
          <ReferralLinksCard refCode={refCode} variant="full" />
        </section>
      )}

      <div className="pt-2">
        <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-3">
          {t.downloads}
        </h3>
        {t.contentNote && items.length > 0 && (
          <p className="-mt-1.5 mb-1 text-[11px] text-gray-500" data-testid="content-lang-note">{t.contentNote}</p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t.search}
            aria-label={t.searchLabel}
            className="w-full min-h-[44px] bg-white/[0.03] border border-white/[0.06] rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#c9a84c]/30 transition-colors"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {categories.map(c => (
            <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)} className={`min-h-[40px] px-3.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 ${category === c ? 'bg-[#c9a84c] text-[#0a1628] shadow-lg shadow-[#c9a84c]/15' : 'bg-white/[0.04] text-gray-400 hover:text-gray-300 hover:bg-white/[0.06] border border-white/[0.06]'}`}>{c === 'Todos' ? t.all : c}</button>
          ))}
        </div>
      </div>
      {error ? (
        <LoadError onRetry={load} text={t.loadError} />
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] py-16 px-6 text-center">
          <Search size={32} className="text-gray-500 mx-auto mb-3" />
          {items.length === 0 ? (
            <p className="text-gray-400 text-sm">{t.empty}</p>
          ) : (
            <>
              <p className="text-gray-400 text-sm">{t.noMatch}</p>
              <button type="button" onClick={() => { setSearch(''); setCategory('Todos'); }} className="mt-3 min-h-[40px] px-4 rounded-full text-xs font-medium text-[#c9a84c] border border-[#c9a84c]/20 hover:bg-[#c9a84c]/10 transition-colors">
                {t.clear}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((r, i) => {
            const Icon = typeIcons[r.type] || File;
            const iconColor = typeColors[r.type] || '#c9a84c';
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(i, 6) * 0.03 }}
                className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 flex items-start gap-4 hover:border-[#c9a84c]/20 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${iconColor}10` }}>
                  <Icon size={17} style={{ color: iconColor }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium text-sm truncate group-hover:text-[#c9a84c] transition-colors duration-200">{r.title}</h3>
                  <p className="text-gray-400 text-xs mt-1">{[r.category, t.type(r.type), r.size].filter(Boolean).join(' · ')}</p>
                  {r.description && <p className="text-gray-500 text-xs mt-1.5 line-clamp-2">{r.description}</p>}
                  {r.locked && (
                    <Link to="/dashboard#membresias" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#c9a84c] hover:underline">
                      {r.min_plan_name ? t.availableWith(r.min_plan_name) : t.availableMembers}
                    </Link>
                  )}
                </div>
                {r.locked ? (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-semibold text-[#c9a84c] bg-[#c9a84c]/[0.06] border border-[#c9a84c]/20 flex-shrink-0" data-testid="resource-locked">
                    <Lock size={12} /> {r.min_plan_name || t.members}
                  </span>
                ) : r.url ? (
                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 min-h-[40px] px-3.5 rounded-full text-[11px] uppercase tracking-wider font-semibold bg-white/[0.04] hover:bg-[#c9a84c]/10 text-gray-400 hover:text-[#c9a84c] border border-white/[0.06] hover:border-[#c9a84c]/20 transition-all duration-200 flex-shrink-0">
                    {r.type === 'Enlace' ? <><ExternalLink size={12} /> {t.open}</> : <><Download size={12} /> {t.download}</>}
                  </a>
                ) : (
                  <span className="px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-semibold text-gray-500 border border-white/[0.06] flex-shrink-0">{t.soon}</span>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
